/**
 * Carbon code snippet in IBM Plex Mono:
 *   inline   a short token in running text (a <code> on the hover layer)
 *   single   one line on layer-01 that scrolls sideways inside itself
 *   multi    a block that wraps long lines and scrolls inside `maxHeight`
 * Single and multi render a <pre class="font-mono whitespace-pre-wrap"> (single
 * adds a no-wrap override) with a CopyButton pinned top right. The block never
 * widens the page: it scrolls inside its own box.
 */
import clsx from 'clsx'
import { CopyButton } from './CopyButton'

export interface CodeSnippetProps {
  kind?: 'inline' | 'single' | 'multi'
  code: string
  /** Shown to screen readers and kept as data-language for highlighting later. */
  language?: string
  /** Multi only: the block scrolls past this height (any CSS length). Default 15 lines. */
  maxHeight?: string
  /** The copy button's accessible name. Default "Copy". */
  copyLabel?: string
  className?: string
}

export function CodeSnippet({ kind = 'multi', code, language, maxHeight = '21.5rem', copyLabel, className }: CodeSnippetProps) {
  if (kind === 'inline') {
    return (
      <code data-language={language} className={clsx('bg-layer-hover px-1 py-px font-mono text-[13px] text-text-primary [overflow-wrap:anywhere]', className)}>
        {code}
      </code>
    )
  }
  const single = kind === 'single'
  return (
    <div data-language={language} className={clsx('relative min-w-0 bg-layer-1', className)}>
      {language ? <span className="sr-only">{language} code</span> : null}
      <pre
        className={clsx(
          'm-0 font-mono text-[14px] leading-[1.43] text-text-primary whitespace-pre-wrap',
          single ? 'overflow-x-auto !whitespace-pre py-[11px] pl-4 pr-14' : 'overflow-y-auto p-4 pr-14 [overflow-wrap:anywhere]',
        )}
        style={single ? undefined : { maxHeight }}
      >
        <code>{code}</code>
      </pre>
      <CopyButton text={code} label={copyLabel} className="absolute right-0 top-0 bg-layer-1" />
    </div>
  )
}

export default CodeSnippet
